import {
  Box,
  Grid,
  keyframes,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { useMemo } from 'react';

interface LoadingProps {
  title?: string;
  description?: string;
  icon?: string;
  iconSize?: number;
  maxWidth?: number | string;
  minHeight?: number | string;
  padding?: number;
  sx?: any;
}

// Animasyon keyframes
const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const wave = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-15px);
  }
`;

export const Loading = ({
  title = 'Yükleniyor',
  description,
  icon = '⚡',
  iconSize = 55,
  maxWidth = 520,
  minHeight = 300,
  padding = 2,
  sx,
}: LoadingProps) => {
  const theme = useTheme();

  // Varsayılan açıklamalar
  const defaultDescriptions = useMemo(
    () => [
      'Verileriniz hazırlanıyor...',
      'Biraz daha bekleyin, neredeyse hazır!',
      'Sihirli veriler yükleniyor...',
      'Verileriniz için sabırla bekliyoruz...',
      'Hemen geliyor, acele etmeyin! 🚀',
      'Sisteminiz için en iyisini hazırlıyoruz...',
    ],
    [],
  );

  // Rastgele açıklama seç
  const randomDescription = useMemo(() => {
    const randomIndex = Math.floor(Math.random() * defaultDescriptions.length);
    return description || defaultDescriptions[randomIndex];
  }, [description, defaultDescriptions]);

  // İkona göre animasyon seç
  const getAnimation = (icon: string) => {
    switch (icon) {
      case '⚡':
        return `${bounce} 2s infinite`;
      case '🔄':
        return `${rotate} 2s linear infinite`;
      case '💫':
        return `${pulse} 1.5s ease-in-out infinite`;
      case '🎯':
        return `${bounce} 1.5s infinite`;
      case '🚀':
        return `${wave} 2s ease-in-out infinite`;
      case '🌟':
        return `${pulse} 2s ease-in-out infinite`;
      case '🎪':
        return `${rotate} 3s linear infinite`;
      case '🎨':
        return `${bounce} 1.8s infinite`;
      default:
        return `${pulse} 2s ease-in-out infinite`;
    }
  };

  // Loading noktaları animasyonu
  const dotsAnimation = keyframes`
    0%, 20% {
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  `;

  return (
    <Grid
      sx={{
        height: '100%',
        flex: 1,
        minHeight,
        padding,
        ...sx,
      }}
      component={Paper}
      justifyContent="center"
      alignItems="center"
      container
    >
      <Box
        sx={{
          textAlign: 'center',
          maxWidth,
          px: 2,
        }}
      >
        {/* Animasyonlu İkon */}
        <Grid justifyContent="center" mb={3} container>
          <Typography
            sx={{
              fontSize: iconSize,
              color: theme.palette.primary.main,
              animation: getAnimation(icon),
              display: 'inline-block',
            }}
          >
            {icon}
          </Typography>
        </Grid>

        {/* Başlık */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            mb: 2,
            fontSize: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          {title}
          <Box
            sx={{
              display: 'inline-flex',
              gap: 0.5,
            }}
          >
            <Typography
              component="span"
              sx={{
                animation: `${dotsAnimation} 1.4s infinite`,
                animationDelay: '0s',
              }}
            >
              .
            </Typography>
            <Typography
              component="span"
              sx={{
                animation: `${dotsAnimation} 1.4s infinite`,
                animationDelay: '0.2s',
              }}
            >
              .
            </Typography>
            <Typography
              component="span"
              sx={{
                animation: `${dotsAnimation} 1.4s infinite`,
                animationDelay: '0.4s',
              }}
            >
              .
            </Typography>
          </Box>
        </Typography>

        {/* Açıklama */}
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            lineHeight: 1.6,
            fontSize: '1rem',
            fontStyle: 'italic',
            opacity: 0.8,
          }}
        >
          {randomDescription}
        </Typography>

        {/* Progress Bar Benzeri Animasyon */}
        <Box
          sx={{
            mt: 4,
            width: '100%',
            height: 4,
            backgroundColor: theme.palette.action.hover,
            borderRadius: 2,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '30%',
              backgroundColor: theme.palette.primary.main,
              borderRadius: 2,
              animation: `${keyframes`
                  0% {
                    left: -30%;
                  }
                  100% {
                    left: 100%;
                  }
                `} 2s ease-in-out infinite`,
            }}
          />
        </Box>

        {/* Eğlenceli Alt Mesaj */}
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            mt: 2,
            display: 'block',
            opacity: 0.6,
            fontSize: '0.75rem',
          }}
        >
          {icon === '⚡' && 'Hızlı yükleme modu aktif!'}
          {icon === '🔄' && 'Veriler döndürülüyor...'}
          {icon === '💫' && 'Sihirli güçler çalışıyor...'}
          {icon === '🎯' && 'Hedef kilitlendi!'}
          {icon === '🚀' && 'Roket hızında yükleniyor...'}
          {icon === '🌟' && 'Yıldız gücüyle yükleniyor...'}
          {icon === '🎪' && 'Sirk gösterisi başlıyor...'}
          {icon === '🎨' && 'Sanat eseri hazırlanıyor...'}
          {!['⚡', '🔄', '💫', '🎯', '🚀', '🌟', '🎪', '🎨'].includes(icon) &&
            'Harika şeyler oluyor...'}
        </Typography>
      </Box>
    </Grid>
  );
};
